import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import type { CrmRequiredActionItem } from '../lib/crm/requiredActions';
import { createBusinessCache } from '../lib/controlCentre/actionsCache';

/** A move the Sales manager has staged on a lead, waiting for the owner's tap. */
export interface SalesPlay {
  id: string;
  opportunity_id: number;
  trigger_event: 'lead_new' | 'follow_up_due' | 'lead_reply' | 'lead_touched' | 'gone_quiet' | 'manual' | 'proposal_approved';
  /** 'none' is a move-only play: stage, follow-up, and/or closing the lead, no message. */
  channel: 'sms' | 'email' | 'call' | 'none';
  to_address: string | null;
  lead_name: string;
  subject: string | null;
  body: string;
  talking_points: string[];
  next_follow_up_at: string | null;
  move_to_stage: string | null;
  /** Close the lead on apply. */
  mark_status: 'won' | 'lost' | null;
  lost_reason: string | null;
  why: string;
  /** What the business's automations will do around this play (scheduled sends, stage-entry automations). */
  automation_note: string | null;
  send_error: string | null;
  created_at: string;
  /** Held quote on this card. Null once it is sent or thrown away. */
  proposal_id: string | null;
  proposal_basis: string | null;
  proposal: { id: string; title: string; total: number } | null;
  /** Accepted quote this play would book. Null when the play is only confirming or asking. */
  booking_proposal_id: string | null;
  booking_date: string | null;
  booking_time: string | null;
  booking_end_date: string | null;
  booking_end_time: string | null;
  booking_resource_id: string | null;
  /** People on the staged booking. The first is who Sales picked; the owner can change the list. */
  booking_resource_ids: string[];
  booking_label: string | null;
  /** Set once the booking exists, so a retry does not show the checkbox again. */
  booking_id: string | null;
  /** Field-quote visit this play would schedule. Null when the play is only asking. */
  field_quote_date: string | null;
  field_quote_time: string | null;
  field_quote_end_date: string | null;
  field_quote_end_time: string | null;
  field_quote_resource_id: string | null;
  field_quote_resource_ids: string[];
  field_quote_label: string | null;
  /** Set once the visit exists, so a retry does not show the checkbox again. */
  field_quote_id: string | null;
}

const PLAY_COLUMNS =
  'id, opportunity_id, trigger_event, channel, to_address, lead_name, subject, body, talking_points, next_follow_up_at, move_to_stage, mark_status, lost_reason, why, automation_note, send_error, created_at, proposal_id, proposal_basis, booking_proposal_id, booking_date, booking_time, booking_end_date, booking_end_time, booking_resource_id, booking_resource_ids, booking_label, booking_id, field_quote_date, field_quote_time, field_quote_end_date, field_quote_end_time, field_quote_resource_id, field_quote_resource_ids, field_quote_label, field_quote_id';

const playsCache = createBusinessCache<SalesPlay[]>({ persistKey: 'sales-plays' });

/**
 * Leads with a staged play but no row of their own in Actions Required still
 * need a row, so the owner can send. These are "waiting on you", not overdue.
 */
export function buildSalesPlayRequiredActions(
  plays: SalesPlay[],
  coveredOpportunityIds: Set<number>,
): CrmRequiredActionItem[] {
  return plays
    .filter((play) => !coveredOpportunityIds.has(play.opportunity_id))
    .map((play) => {
      const created = new Date(play.created_at);
      return {
        kind: 'sales_play' as const,
        opportunityId: play.opportunity_id,
        salesPlayId: play.id,
        title: play.lead_name || 'Lead',
        customerName: play.lead_name || 'Lead',
        subtitle: 'Sales staged the next move',
        sortOrder: -created.getTime(),
        createdAt: play.created_at,
      };
    });
}

/** Staged Sales plays for the current business, live. */
export function useSalesPlays(enabled = true) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [plays, setPlays] = useState<SalesPlay[]>(() => playsCache.get(businessId) ?? []);
  const [isLoading, setIsLoading] = useState(() => !playsCache.has(businessId));
  // Plays the owner just tapped. A refetch while the send is still running must not put them back.
  const pendingRef = useRef(new Set<string>());
  const restoreErrorRef = useRef(new Map<string, string>());
  const playsRef = useRef(plays);
  playsRef.current = plays;
  const opportunityByPlayRef = useRef(new Map<string, number>());
  const [pendingOpportunityIds, setPendingOpportunityIds] = useState<Set<number>>(() => new Set());

  const fetchPlays = useCallback(async () => {
    if (!businessId || !supabase) return;
    const { data, error } = await supabase
      .from('sales_plays')
      .select(PLAY_COLUMNS)
      .eq('business_id', businessId)
      .eq('status', 'staged')
      .order('created_at', { ascending: false })
      .limit(200);
    if (error) throw error;
    const rows = ((data ?? []) as unknown as SalesPlay[]).map((row) => ({
      ...row,
      talking_points: Array.isArray(row.talking_points) ? row.talking_points : [],
      proposal_id: row.proposal_id ?? null,
      proposal_basis: row.proposal_basis ?? null,
      proposal: null,
      booking_proposal_id: row.booking_proposal_id ?? null,
      booking_date: row.booking_date ?? null,
      booking_time: row.booking_time ?? null,
      booking_end_date: row.booking_end_date ?? null,
      booking_end_time: row.booking_end_time ?? null,
      booking_resource_id: row.booking_resource_id ?? null,
      booking_resource_ids: Array.isArray(row.booking_resource_ids) ? row.booking_resource_ids : [],
      booking_label: row.booking_label ?? null,
      booking_id: row.booking_id ?? null,
      field_quote_date: row.field_quote_date ?? null,
      field_quote_time: row.field_quote_time ?? null,
      field_quote_end_date: row.field_quote_end_date ?? null,
      field_quote_end_time: row.field_quote_end_time ?? null,
      field_quote_resource_id: row.field_quote_resource_id ?? null,
      field_quote_resource_ids: Array.isArray(row.field_quote_resource_ids) ? row.field_quote_resource_ids : [],
      field_quote_label: row.field_quote_label ?? null,
      field_quote_id: row.field_quote_id ?? null,
    }));
    const quoteIds = [...new Set(rows.map((row) => row.proposal_id).filter((id): id is string => Boolean(id)))];
    if (quoteIds.length) {
      const { data: quotes } = await supabase
        .from('proposals')
        .select('id, title, total_amount, status')
        .in('id', quoteIds)
        .eq('status', 'held');
      const byId = new Map((quotes ?? []).map((quote) => [quote.id, quote]));
      for (const row of rows) {
        const quote = row.proposal_id ? byId.get(row.proposal_id) : undefined;
        row.proposal = quote
          ? { id: quote.id, title: quote.title || 'Proposal', total: Number(quote.total_amount) || 0 }
          : null;
      }
    }
    const visible = rows
      .filter((row) => !pendingRef.current.has(row.id))
      .map((row) => {
        const note = restoreErrorRef.current.get(row.id);
        return note ? { ...row, send_error: row.send_error || note } : row;
      });
    setPlays(visible);
    playsCache.set(businessId, visible);
  }, [businessId, supabase]);

  const load = useCallback(async () => {
    if (!enabled || !businessId || !supabase) {
      setPlays([]);
      setIsLoading(false);
      return;
    }
    if (playsCache.has(businessId)) {
      setPlays(playsCache.get(businessId) ?? []);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    try {
      await fetchPlays();
    } catch (err) {
      console.error('Error loading sales plays:', err);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId, supabase, fetchPlays]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setPlays([]);
      return;
    }
    void load();
  }, [load, enabled]);

  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !businessId || !supabase) return;
    const pull = () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      refreshDebounceRef.current = setTimeout(() => {
        void fetchPlays();
      }, 300);
    };
    const channel = supabase
      .channel(`sales-plays-${businessId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sales_plays', filter: `business_id=eq.${businessId}` },
        pull,
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') pull();
      });
    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, businessId, supabase, fetchPlays]);

  const playsByOpportunity = useMemo(() => {
    const map = new Map<number, SalesPlay>();
    // A play the owner asked for in chat stays on the Send all card. Needs your
    // attention is the pipeline: a lead was new, replied, came due, or a quote was approved.
    for (const play of plays) {
      if (play.trigger_event === 'manual') continue;
      if (!map.has(play.opportunity_id)) map.set(play.opportunity_id, play);
    }
    return map;
  }, [plays]);

  const hideOpportunity = useCallback((playId: string) => {
    const play = playsRef.current.find((row) => row.id === playId);
    if (!play) return;
    opportunityByPlayRef.current.set(playId, play.opportunity_id);
    setPendingOpportunityIds((current) => {
      const next = new Set(current);
      next.add(play.opportunity_id);
      return next;
    });
  }, []);

  const showOpportunity = useCallback((playId: string) => {
    const opportunityId = opportunityByPlayRef.current.get(playId);
    opportunityByPlayRef.current.delete(playId);
    if (opportunityId == null) return;
    setPendingOpportunityIds((current) => {
      const next = new Set(current);
      next.delete(opportunityId);
      return next;
    });
  }, []);

  /** Drop the card the instant Send, Done, or Skip is pressed. */
  const holdPlay = useCallback((playId: string) => {
    pendingRef.current.add(playId);
    restoreErrorRef.current.delete(playId);
    hideOpportunity(playId);
    setPlays((current) => {
      const next = current.filter((play) => play.id !== playId);
      if (businessId) playsCache.set(businessId, next);
      return next;
    });
  }, [businessId, hideOpportunity]);

  /** The send finished. The lead list can show whatever the server left behind. */
  const settlePlay = useCallback((playId: string) => {
    pendingRef.current.delete(playId);
    restoreErrorRef.current.delete(playId);
    showOpportunity(playId);
    void fetchPlays();
  }, [fetchPlays, showOpportunity]);

  /** The send failed. Put the card back with the reason. */
  const failPlay = useCallback((playId: string, message: string) => {
    pendingRef.current.delete(playId);
    restoreErrorRef.current.set(playId, message);
    void fetchPlays().finally(() => showOpportunity(playId));
  }, [fetchPlays, showOpportunity]);

  return { plays, playsByOpportunity, isLoading, refresh: load, holdPlay, settlePlay, failPlay, pendingOpportunityIds };
}
